import { Link, useRouterState } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { useQuarantine, useReminders, useSystemStatus } from '../api/hooks'
import { Badge, useDarkMode } from './ui'

const NAV = [
  { to: '/', label: 'Übersicht' },
  { to: '/dokumente', label: 'Dokumente' },
  { to: '/vorgaenge', label: 'Vorgänge' },
  { to: '/suche', label: 'Suche' },
  { to: '/spam', label: 'Spam & Betrug' },
  { to: '/erinnerungen', label: 'Erinnerungen' },
  { to: '/brief', label: 'Brief hochladen' },
  { to: '/einstellungen', label: 'Einstellungen' },
] as const

export function Layout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth()
  const [dark, setDark] = useDarkMode()
  const [menuOpen, setMenuOpen] = useState(false)
  const { location } = useRouterState()
  const { data: due } = useReminders('due')
  const { data: status } = useSystemStatus()
  const { data: toReview } = useQuarantine('suspected')

  useEffect(() => setMenuOpen(false), [location.pathname])

  // Keyboard shortcut: "/" jumps to search from anywhere.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const typing = target && ['INPUT', 'TEXTAREA'].includes(target.tagName)
      if (event.key === '/' && !typing) {
        event.preventDefault()
        window.location.assign('/suche')
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const dueCount = due?.length ?? 0
  const reviewCount = toReview?.total ?? 0
  const tiersOnline = (status?.tiers ?? []).filter((tier) => tier.online).length

  return (
    <div className="min-h-full">
      <a
        href="#inhalt"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-white focus:px-3 focus:py-2"
      >
        Zum Inhalt springen
      </a>

      <header className="sticky top-0 z-30 border-b border-paper-200 bg-paper-50/90 backdrop-blur dark:border-paper-800 dark:bg-paper-950/90">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3">
          <Link to="/" className="flex items-center gap-2 font-display text-lg tracking-tight">
            <img src="/icon.svg" alt="" aria-hidden width={24} height={24} className="rounded" />
            Backoffice
          </Link>

          <nav aria-label="Hauptnavigation" className="ml-4 hidden flex-1 md:block">
            <ul className="flex flex-wrap gap-1">
              {NAV.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="rounded-md px-2.5 py-1.5 text-sm text-paper-600 hover:bg-paper-100 dark:text-paper-300 dark:hover:bg-paper-800 [&.active]:bg-paper-200 [&.active]:text-paper-900 dark:[&.active]:bg-paper-800 dark:[&.active]:text-white"
                    activeOptions={{ exact: item.to === '/' }}
                  >
                    {item.label}
                    {item.to === '/erinnerungen' && dueCount > 0 ? (
                      <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 text-xs text-white">
                        {dueCount}
                      </span>
                    ) : null}
                    {item.to === '/spam' && reviewCount > 0 ? (
                      <span
                        className="ml-1.5 rounded-full bg-red-600 px-1.5 text-xs text-white"
                        title="Nachrichten, bei denen Backoffice sich nicht sicher ist"
                      >
                        {reviewCount}
                      </span>
                    ) : null}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <span className="hidden text-xs text-paper-500 sm:inline" title="Verfügbare Modell-Tiers">
              {tiersOnline > 0 ? `${tiersOnline} Tier online` : 'kein Modell online'}
            </span>
            <button
              type="button"
              className="btn-ghost px-2"
              aria-pressed={dark}
              onClick={() => setDark(!dark)}
            >
              {dark ? 'Hell' : 'Dunkel'}
            </button>
            <button
              type="button"
              className="btn-ghost px-2 md:hidden"
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
              onClick={() => setMenuOpen((open) => !open)}
            >
              Menü
            </button>
            {user ? (
              <button type="button" className="btn-secondary hidden md:inline-flex" onClick={() => void logout()}>
                Abmelden
              </button>
            ) : null}
          </div>
        </div>

        {menuOpen ? (
          <nav id="mobile-nav" aria-label="Hauptnavigation mobil" className="border-t border-paper-200 md:hidden dark:border-paper-800">
            <ul className="mx-auto max-w-6xl px-4 py-2">
              {NAV.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="block rounded px-2 py-2 text-sm">
                    {item.label}
                    {item.to === '/erinnerungen' && dueCount > 0 ? (
                      <Badge tone="warn">{dueCount} fällig</Badge>
                    ) : null}
                  </Link>
                </li>
              ))}
              <li>
                <button type="button" className="block w-full rounded px-2 py-2 text-left text-sm" onClick={() => void logout()}>
                  Abmelden
                </button>
              </li>
            </ul>
          </nav>
        ) : null}
      </header>

      <main id="inhalt" className="mx-auto max-w-6xl animate-fade-in px-4 py-6">
        {children}
      </main>

      <footer className="mx-auto max-w-6xl px-4 pb-8 text-xs text-paper-400">
        Backoffice · self-hosted · alle Daten bleiben auf diesem Server
      </footer>
    </div>
  )
}
